import { DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  filter,
  firstValueFrom,
  fromEvent,
  merge,
  Subscription,
  timer,
} from 'rxjs';
import { TicketApiService } from '../data-access/ticket-api.service';
import {
  Ticket,
  TicketMessage,
  TicketReplyPayload,
  TicketSenderRole,
} from '../domain/ticket.models';
import {
  isOwnTicketMessage,
  mergeTicketMessages,
} from '../domain/ticket-message-state';

const PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 5000;

@Injectable()
export class TicketConversationStore implements OnDestroy {
  private readonly api = inject(TicketApiService);
  private readonly document = inject(DOCUMENT);

  private readonly ticketState = signal<Ticket | null>(null);
  private readonly messagesState = signal<TicketMessage[]>([]);
  private readonly loadingState = signal(false);
  private readonly sendingState = signal(false);
  private readonly pollErrorState = signal(false);

  readonly ticket = this.ticketState.asReadonly();
  readonly messages = this.messagesState.asReadonly();
  readonly loading = this.loadingState.asReadonly();
  readonly sending = this.sendingState.asReadonly();
  readonly pollError = this.pollErrorState.asReadonly();

  private ticketNumber: string | null = null;
  private senderRole = TicketSenderRole.USER;
  private senderId: string | null = null;
  private tailPage = 1;
  private session = 0;
  private requestInFlight = false;
  private polling = Subscription.EMPTY;

  async open(
    ticketNumber: string,
    senderRole: TicketSenderRole,
    senderId?: string | null,
  ): Promise<void> {
    this.close();
    const session = this.session;
    this.ticketNumber = ticketNumber;
    this.senderRole = senderRole;
    this.senderId = senderId ?? null;
    this.loadingState.set(true);

    try {
      await this.fetchPagesFrom(1, session);
      if (!this.isActive(session)) return;
      try {
        await this.markCounterpartMessagesRead(session);
      } catch {
        if (this.isActive(session)) this.pollErrorState.set(true);
      }
      if (this.isActive(session)) this.startPolling(session);
    } finally {
      if (this.isActive(session)) this.loadingState.set(false);
    }
  }

  close(): void {
    this.session += 1;
    this.polling.unsubscribe();
    this.polling = Subscription.EMPTY;
    this.ticketNumber = null;
    this.senderId = null;
    this.tailPage = 1;
    this.requestInFlight = false;
    this.ticketState.set(null);
    this.messagesState.set([]);
    this.loadingState.set(false);
    this.sendingState.set(false);
    this.pollErrorState.set(false);
  }

  async reply(payload: TicketReplyPayload): Promise<TicketMessage | null> {
    const ticketNumber = this.ticketNumber;
    const session = this.session;
    if (!ticketNumber || this.sendingState()) return null;

    this.sendingState.set(true);
    try {
      const message = await firstValueFrom(
        this.api.reply(ticketNumber, payload),
      );
      if (!this.isActive(session)) return null;
      const optimisticMessage = { ...message, isRead: false };
      this.mergeMessages([optimisticMessage]);
      return optimisticMessage;
    } finally {
      if (this.isActive(session)) this.sendingState.set(false);
    }
  }

  ngOnDestroy(): void {
    this.close();
  }

  private startPolling(session: number): void {
    this.polling.unsubscribe();
    const intervalTicks = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS);
    const visibleTicks = fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
    );
    this.polling = merge(intervalTicks, visibleTicks).subscribe(() => {
      void this.poll(session);
    });
  }

  private async poll(session: number): Promise<void> {
    if (
      !this.isActive(session) ||
      this.requestInFlight ||
      this.document.visibilityState !== 'visible'
    ) {
      return;
    }

    this.requestInFlight = true;
    try {
      await this.fetchPagesFrom(this.tailPage, session);
      await this.markCounterpartMessagesRead(session);
      if (this.isActive(session)) this.pollErrorState.set(false);
    } catch {
      if (this.isActive(session)) this.pollErrorState.set(true);
    } finally {
      if (this.isActive(session)) this.requestInFlight = false;
    }
  }

  private async fetchPagesFrom(
    startPage: number,
    session: number,
  ): Promise<void> {
    const ticketNumber = this.ticketNumber;
    if (!ticketNumber) return;

    let page = startPage;
    while (this.isActive(session)) {
      const response = await firstValueFrom(
        this.api.getDetails(ticketNumber, page, PAGE_SIZE),
      );
      if (!this.isActive(session)) return;
      this.ticketState.set(response.ticket);
      this.mergeMessages(response.messages);
      if (response.messages.length < PAGE_SIZE) {
        this.tailPage = page;
        return;
      }
      page += 1;
    }
  }

  private async markCounterpartMessagesRead(session: number): Promise<void> {
    const unreadIds = this.messagesState()
      .filter(
        (message) =>
          !message.isRead &&
          !isOwnTicketMessage(message, this.senderRole, this.senderId),
      )
      .map((message) => message.id);
    if (!unreadIds.length) return;

    await firstValueFrom(this.api.markAsRead(unreadIds));
    if (!this.isActive(session)) return;
    const readIds = new Set(unreadIds);
    this.messagesState.update((messages) =>
      messages.map((message) =>
        readIds.has(message.id) ? { ...message, isRead: true } : message,
      ),
    );
  }

  private mergeMessages(incoming: TicketMessage[]): void {
    this.messagesState.update((current) =>
      mergeTicketMessages(current, incoming),
    );
  }

  private isActive(session: number): boolean {
    return session === this.session && this.ticketNumber !== null;
  }
}
